import React, { useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle, Shield, Eye, FileCheck, Activity } from 'lucide-react';
import { ScanFinding } from '../types/scan.ts';
import { ResultCard } from './ResultCard.tsx';

interface CollapsibleResultGroupProps {
  findings: ScanFinding[];
  title: string;
  description?: string;
  className?: string;
  isProUser?: boolean;
  defaultExpanded?: boolean;
  onJumpToFinding?: (finding: ScanFinding) => void;
}

// Group similar findings by file and issue type
const groupSimilarFindings = (findings: ScanFinding[]): Array<{ 
  key: string;
  findings: ScanFinding[];
  file_path: string;
  issue_type: string;
  severity: string;
}> => {
  const grouped = new Map<string, ScanFinding[]>();
  
  findings.forEach(finding => {
    // Create a key based on file path and issue type to group similar findings
    const key = `${finding.file_path}|${finding.issue_type}|${finding.module_id}`;
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(finding);
  });
  
  return Array.from(grouped.entries()).map(([key, groupedFindings]) => {
    const [file_path, issue_type] = key.split('|');
    return {
      key,
      findings: groupedFindings,
      file_path,
      issue_type,
      severity: groupedFindings[0].severity // Use first finding's severity
    };
  }).sort((a, b) => {
    // Sort by severity (critical -> high -> medium -> low -> info)
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
    const aSeverity = severityOrder[a.severity as keyof typeof severityOrder] ?? 4;
    const bSeverity = severityOrder[b.severity as keyof typeof severityOrder] ?? 4;
    return aSeverity - bSeverity;
  });
};

const getSeverityIcon = (severity: string) => {
  switch (severity.toLowerCase()) {
    case 'critical':
    case 'high':
      return <AlertTriangle className="w-4 h-4 text-red-400" />;
    case 'medium':
      return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
    case 'low':
    case 'info':
      return <AlertTriangle className="w-4 h-4 text-blue-400" />;
    default:
      return <AlertTriangle className="w-4 h-4 text-gray-400" />;
  }
};

const getSeverityColor = (severity: string): string => {
  switch (severity.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'border-red-500 bg-red-900/10';
    case 'medium':
      return 'border-yellow-500 bg-yellow-900/10';
    case 'low':
    case 'info':
      return 'border-blue-500 bg-blue-900/10';
    default:
      return 'border-gray-500 bg-gray-900/10';
  }
};

export const CollapsibleResultGroup: React.FC<CollapsibleResultGroupProps> = ({
  findings,
  title,
  description,
  className = '',
  isProUser = false,
  defaultExpanded = false,
  onJumpToFinding
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  
  if (findings.length === 0) {
    return null;
  }

  const groupedFindings = groupSimilarFindings(findings);
  const totalFindings = findings.length;
  
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  
  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  return (
    <div className={`border border-gray-700 rounded-lg bg-gray-800/50 ${className}`}>
      {/* Group Header */}
      <button
        onClick={toggleExpanded}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-700/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
          <div className="text-left">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            {description && (
              <p className="text-sm text-gray-400">{description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-300">
            {groupedFindings.length} unique issue{groupedFindings.length !== 1 ? 's' : ''}
            {totalFindings !== groupedFindings.length && (
              <span className="text-gray-500 ml-1">
                ({totalFindings} total)
              </span>
            )}
          </div>
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-700 p-4 space-y-3">
          {groupedFindings.map((group) => {
            const isGroupExpanded = expandedGroups.has(group.key);
            const hasMultiple = group.findings.length > 1;
            
            return (
              <div 
                key={group.key} 
                className={`border rounded-lg ${getSeverityColor(group.severity)}`}
              >
                {/* Individual Group Header */}
                <div className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      {getSeverityIcon(group.severity)}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">
                          {group.issue_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </div>
                        <div className="text-xs text-gray-400 truncate">
                          📁 {group.file_path}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {hasMultiple && (
                        <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-full">
                          {group.findings.length}x
                        </span>
                      )}
                      {hasMultiple && (
                        <button
                          onClick={() => toggleGroup(group.key)}
                          className="p-1 hover:bg-gray-600 rounded transition-colors"
                        >
                          {isGroupExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Show first finding by default */}
                  {!hasMultiple || !isGroupExpanded ? (
                    <div className="mt-3">
                      <ResultCard 
                        finding={group.findings[0]} 
                        isProUser={isProUser}
                        showUpgradeButton={false}
                        onJumpToFinding={onJumpToFinding}
                      />
                    </div>
                  ) : null}
                </div>
                
                {/* Show all findings when expanded */}
                {hasMultiple && isGroupExpanded && (
                  <div className="border-t border-gray-600/50 p-3 bg-gray-900/20 space-y-3">
                    <div className="text-xs text-gray-400 mb-2">
                      All {group.findings.length} instances:
                    </div>
                    {group.findings.map((finding, index) => (
                      <div key={finding.id} className="pl-4 border-l-2 border-gray-600">
                        <div className="text-xs text-gray-500 mb-1">
                          Instance {index + 1}
                        </div>
                        <ResultCard 
                          finding={finding} 
                          isProUser={isProUser}
                          showUpgradeButton={false}
                          onJumpToFinding={onJumpToFinding}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}; 